/**
 * ContentTool.ts
 * Manages content placement (enemies, traps, loot, props) in dungeon cells.
 * V1: Click palette item → select cell → click anchor in Properties panel.
 *
 * Content is stored as string IDs in cell.spawns/traps/loot/decorations arrays.
 * The CONTENT_PALETTE registry maps IDs to display info and energy costs.
 */

import type { GridCell, OctileCell, SquareConnector, CellType } from '../types/OctileTypes.ts'
import { SAFE_ZONE_TYPES } from '../types/OctileTypes.ts'
import { CELL_LIMITS } from '../types/ContentTypes.ts'
import type { AnchorDirection } from '../types/ContentTypes.ts'
import { getCell } from '../grid/OctileGrid.ts'

// ─── Types ──────────────────────────────────────────────────

export type ContentCategory = 'enemies' | 'traps' | 'loot' | 'props'

export interface ContentPaletteItem {
    id: string
    label: string
    category: ContentCategory
    energyCost: number
    icon: string
    description: string
    appliesTo: 'octagon' | 'connector' | 'both'
}

export interface PlacedContent {
    id: string
    category: ContentCategory
    item: ContentPaletteItem
}

export interface ContentRestrictions {
    allowEnemies: boolean
    allowTraps: boolean
    maxEnemies: number
    maxTraps: number
}

// ─── Content Palette Data ───────────────────────────────────

export const CONTENT_PALETTE: ContentPaletteItem[] = [
    // ─── Enemies ───
    { id: 'skeleton_sword', label: 'Skeleton (Sword)', category: 'enemies',
      energyCost: 15, icon: '\u{1F480}', description: 'Basic melee minion', appliesTo: 'octagon' },
    { id: 'skeleton_archer', label: 'Skeleton (Archer)', category: 'enemies',
      energyCost: 20, icon: '\u{1F3F9}', description: 'Ranged minion', appliesTo: 'octagon' },
    { id: 'spider', label: 'Giant Spider', category: 'enemies',
      energyCost: 25, icon: '\u{1F577}', description: 'Fast melee, webs', appliesTo: 'both' },
    { id: 'golem', label: 'Stone Golem', category: 'enemies',
      energyCost: 60, icon: '\u{1F5FF}', description: 'Slow elite, high HP', appliesTo: 'octagon' },
    { id: 'shadow_lurker', label: 'Shadow Lurker', category: 'enemies',
      energyCost: 45, icon: '\u{1F464}', description: 'Invisible until close', appliesTo: 'octagon' },

    // ─── Traps ───
    { id: 'pressure_plate', label: 'Pressure Plate', category: 'traps',
      energyCost: 15, icon: '\u2B07', description: 'Floor trigger, spike damage', appliesTo: 'both' },
    { id: 'arrow_wall', label: 'Arrow Wall', category: 'traps',
      energyCost: 30, icon: '\u27A1', description: 'Wall-mounted projectiles', appliesTo: 'octagon' },
    { id: 'poison_gas', label: 'Poison Gas', category: 'traps',
      energyCost: 30, icon: '\u2601', description: 'Area DoT cloud', appliesTo: 'octagon' },
    { id: 'flame_jet', label: 'Flame Jet', category: 'traps',
      energyCost: 50, icon: '\u{1F525}', description: 'High damage, timed', appliesTo: 'octagon' },
    { id: 'alarm', label: 'Alarm Trap', category: 'traps',
      energyCost: 40, icon: '\u{1F514}', description: 'Spawns enemies when triggered', appliesTo: 'both' },

    // ─── Loot ───
    { id: 'barrel', label: 'Barrel', category: 'loot',
      energyCost: 5, icon: '\u{1F6E2}', description: 'Common container', appliesTo: 'both' },
    { id: 'chest_wooden', label: 'Wooden Chest', category: 'loot',
      energyCost: 15, icon: '\u{1F4E6}', description: 'Uncommon loot', appliesTo: 'octagon' },
    { id: 'chest_ornate', label: 'Ornate Chest', category: 'loot',
      energyCost: 30, icon: '\u2728', description: 'Rare+ loot', appliesTo: 'octagon' },
    { id: 'pedestal', label: 'Pedestal', category: 'loot',
      energyCost: 40, icon: '\u{1F3DB}', description: 'Legendary display', appliesTo: 'octagon' },

    // ─── Props ───
    { id: 'torch', label: 'Wall Torch', category: 'props',
      energyCost: 5, icon: '\u{1F526}', description: 'Light source', appliesTo: 'both' },
    { id: 'pillar', label: 'Stone Pillar', category: 'props',
      energyCost: 10, icon: '\u{1F3DB}', description: 'Cover, blocks movement', appliesTo: 'octagon' },
    { id: 'altar', label: 'Altar', category: 'props',
      energyCost: 20, icon: '\u26E9', description: 'Shrine furniture', appliesTo: 'octagon' },
    { id: 'cage', label: 'Cage', category: 'props',
      energyCost: 10, icon: '\u{1F512}', description: 'Prison decor or trap', appliesTo: 'octagon' },
]

// Lookup map for fast ID → item resolution
const PALETTE_MAP = new Map<string, ContentPaletteItem>()
for (const item of CONTENT_PALETTE) {
    PALETTE_MAP.set(item.id, item)
}

/** Look up a palette item by ID */
export function getPaletteItem(id: string): ContentPaletteItem | undefined {
    return PALETTE_MAP.get(id)
}

// ─── Content Restrictions ───────────────────────────────────

export function getContentRestrictions(cellType: CellType, cellKind: 'octagon' | 'connector'): ContentRestrictions {
    const isSafeZone = (SAFE_ZONE_TYPES as readonly string[]).includes(cellType)

    if (cellKind === 'connector') {
        return {
            allowEnemies: false,  // connectors never have spawns
            allowTraps: true,
            maxEnemies: 0,
            maxTraps: CELL_LIMITS.maxTrapsPerConnector,
        }
    }

    return {
        allowEnemies: !isSafeZone,
        allowTraps: !isSafeZone,
        maxEnemies: isSafeZone ? 0 : CELL_LIMITS.maxEnemiesPerOctagon,
        maxTraps: isSafeZone ? 0 : CELL_LIMITS.maxTrapsPerOctagon,
    }
}

// ─── ContentTool ────────────────────────────────────────────

export class ContentTool {
    private grid: GridCell[][]
    private paletteEl: HTMLElement
    private onEnergyChanged: () => void
    private onStatusUpdate: (msg: string) => void
    private onPaletteSelectionChanged: () => void
    private selectedItem: ContentPaletteItem | null = null
    private filter: ContentCategory | 'all' = 'all'

    constructor(
        grid: GridCell[][],
        paletteElement: HTMLElement,
        onEnergyChanged: () => void,
        onStatusUpdate: (msg: string) => void = () => {},
        onPaletteSelectionChanged: () => void = () => {},
    ) {
        this.grid = grid
        this.paletteEl = paletteElement
        this.onEnergyChanged = onEnergyChanged
        this.onStatusUpdate = onStatusUpdate
        this.onPaletteSelectionChanged = onPaletteSelectionChanged
    }

    // ─── Public API ────────────────────────────────────

    setGrid(grid: GridCell[][]): void {
        this.grid = grid
    }

    getSelectedItem(): ContentPaletteItem | null {
        return this.selectedItem
    }

    selectItem(item: ContentPaletteItem | null): void {
        this.selectedItem = item
        this.updatePaletteSelection()
        this.onPaletteSelectionChanged()
    }

    clearSelection(): void {
        this.selectedItem = null
        this.updatePaletteSelection()
        this.onPaletteSelectionChanged()
    }

    setFilter(category: ContentCategory | 'all'): void {
        this.filter = category
        this.renderPaletteItems()
        this.updatePaletteSelection()
    }

    /** Check if placement is valid */
    canPlace(
        row: number, col: number,
        item: ContentPaletteItem
    ): { valid: boolean; reason?: string } {
        const cell = getCell(this.grid, row, col)
        if (!cell?.active) return { valid: false, reason: 'Cell not active' }

        const cellType = cell.kind === 'octagon' ? (cell as OctileCell).type : 'passage'
        const restrictions = getContentRestrictions(cellType, cell.kind as 'octagon' | 'connector')

        // Cell kind compatibility
        if (item.appliesTo === 'octagon' && cell.kind !== 'octagon') {
            return { valid: false, reason: 'This item can only be placed in octagon rooms' }
        }

        // Category-specific checks
        if (item.category === 'enemies') {
            if (cell.kind === 'connector') {
                return { valid: false, reason: 'Connectors cannot have enemies' }
            }
            if (!restrictions.allowEnemies) {
                return { valid: false, reason: 'Safe zone \u2014 no enemies allowed' }
            }
            if ((cell as OctileCell).spawns.length >= restrictions.maxEnemies) {
                return { valid: false, reason: `Max ${restrictions.maxEnemies} enemies per cell` }
            }
        }

        if (item.category === 'traps') {
            if (!restrictions.allowTraps) {
                return { valid: false, reason: 'Safe zone \u2014 no traps allowed' }
            }
            const trapCount = cell.kind === 'octagon'
                ? (cell as OctileCell).traps.length
                : (cell as SquareConnector).traps.length
            if (trapCount >= restrictions.maxTraps) {
                return { valid: false, reason: `Max ${restrictions.maxTraps} trap(s) per cell` }
            }
        }

        // Loot and props only in octagons (connectors have no loot array)
        if (item.category === 'loot' && cell.kind === 'connector') {
            return { valid: false, reason: 'Connectors cannot hold loot' }
        }

        return { valid: true }
    }

    /** Place content in a cell. Returns false if validation fails. */
    placeContent(row: number, col: number, item: ContentPaletteItem): boolean {
        const check = this.canPlace(row, col, item)
        if (!check.valid) {
            this.onStatusUpdate(check.reason || 'Cannot place here')
            return false
        }

        const cell = getCell(this.grid, row, col)!

        if (cell.kind === 'octagon') {
            const oct = cell as OctileCell
            switch (item.category) {
                case 'enemies': oct.spawns.push(item.id); break
                case 'traps':   oct.traps.push(item.id); break
                case 'loot':    oct.loot.push(item.id); break
                case 'props':   oct.decorations.push(item.id); break
            }
        } else {
            const conn = cell as SquareConnector
            switch (item.category) {
                case 'traps': conn.traps.push(item.id); break
                case 'props': conn.decorations.push(item.id); break
            }
        }

        this.onEnergyChanged()
        this.onStatusUpdate(`Placed ${item.label} in [${row}, ${col}]`)
        return true
    }

    /** Remove content from a cell by ID and category. */
    removeContent(row: number, col: number, contentId: string, category: ContentCategory): boolean {
        const cell = getCell(this.grid, row, col)
        if (!cell?.active) return false

        let arr: string[]
        if (cell.kind === 'octagon') {
            const oct = cell as OctileCell
            switch (category) {
                case 'enemies': arr = oct.spawns; break
                case 'traps':   arr = oct.traps; break
                case 'loot':    arr = oct.loot; break
                case 'props':   arr = oct.decorations; break
            }
        } else {
            const conn = cell as SquareConnector
            switch (category) {
                case 'traps': arr = conn.traps; break
                case 'props': arr = conn.decorations; break
                default: return false // connectors only have traps + decorations
            }
        }

        const idx = arr.indexOf(contentId)
        if (idx === -1) return false

        arr.splice(idx, 1)
        this.onEnergyChanged()

        const item = getPaletteItem(contentId)
        this.onStatusUpdate(`Removed ${item?.label || contentId} from [${row}, ${col}]`)
        return true
    }

    /** Get all content placed in a cell, resolved to palette items. */
    getCellContent(row: number, col: number): PlacedContent[] {
        const cell = getCell(this.grid, row, col)
        if (!cell?.active) return []

        const result: PlacedContent[] = []

        if (cell.kind === 'octagon') {
            const oct = cell as OctileCell
            for (const id of oct.spawns) {
                const item = getPaletteItem(id)
                if (item) result.push({ id, category: 'enemies', item })
            }
            for (const id of oct.traps) {
                const item = getPaletteItem(id)
                if (item) result.push({ id, category: 'traps', item })
            }
            for (const id of oct.loot) {
                const item = getPaletteItem(id)
                if (item) result.push({ id, category: 'loot', item })
            }
            for (const id of oct.decorations) {
                const item = getPaletteItem(id)
                if (item) result.push({ id, category: 'props', item })
            }
        } else {
            const conn = cell as SquareConnector
            for (const id of conn.traps) {
                const item = getPaletteItem(id)
                if (item) result.push({ id, category: 'traps', item })
            }
            for (const id of conn.decorations) {
                const item = getPaletteItem(id)
                if (item) result.push({ id, category: 'props', item })
            }
        }

        return result
    }

    // ─── Palette UI ────────────────────────────────────

    /** Build the palette UI into the container element */
    renderPalette(): void {
        // Tab bar
        const tabs = document.createElement('div')
        tabs.className = 'palette-tabs'

        const categories: (ContentCategory | 'all')[] = ['all', 'enemies', 'traps', 'loot', 'props']
        const tabLabels: Record<string, string> = {
            all: 'All', enemies: 'Enemies', traps: 'Traps', loot: 'Loot', props: 'Props'
        }

        for (const cat of categories) {
            const btn = document.createElement('button')
            btn.className = `palette-tab${cat === this.filter ? ' active' : ''}`
            btn.dataset.cat = cat
            btn.textContent = tabLabels[cat]
            btn.addEventListener('click', () => this.setFilter(cat))
            tabs.appendChild(btn)
        }

        // Items container
        const itemsEl = document.createElement('div')
        itemsEl.className = 'palette-items'
        itemsEl.id = 'paletteItems'

        this.paletteEl.innerHTML = ''
        this.paletteEl.appendChild(tabs)
        this.paletteEl.appendChild(itemsEl)

        this.renderPaletteItems()
    }

    private renderPaletteItems(): void {
        const container = this.paletteEl.querySelector('.palette-items')
        if (!container) return

        const items = this.filter === 'all'
            ? CONTENT_PALETTE
            : CONTENT_PALETTE.filter(i => i.category === this.filter)

        container.innerHTML = items.map(item => `
            <div class="palette-item${this.selectedItem?.id === item.id ? ' palette-item-selected' : ''}"
                 data-item-id="${item.id}" title="${item.description}">
                <span class="palette-item-icon">${item.icon}</span>
                <span class="palette-item-label">${item.label}</span>
                <span class="palette-item-cost">${item.energyCost}</span>
            </div>
        `).join('')

        // Wire click handlers
        container.querySelectorAll('.palette-item').forEach(el => {
            el.addEventListener('click', () => {
                const id = (el as HTMLElement).dataset.itemId!
                const item = getPaletteItem(id)!
                if (this.selectedItem?.id === id) {
                    this.clearSelection()
                    this.onStatusUpdate('Content tool deselected')
                } else {
                    this.selectItem(item)
                    this.onStatusUpdate(`Selected: ${item.label} (${item.energyCost} energy)`)
                }
            })
        })
    }

    private updatePaletteSelection(): void {
        const items = this.paletteEl.querySelectorAll('.palette-item')
        items.forEach(el => {
            const id = (el as HTMLElement).dataset.itemId
            el.classList.toggle('palette-item-selected', id === this.selectedItem?.id)
        })
    }
}
